/* eslint-disable react/prop-types */
// eslint-disable-next-line no-unused-vars
import { h } from 'preact';
import { useRef, useEffect } from 'preact/hooks';

/**
 * Use listener handler.
 *
 * @param {*} ref
 * @param {*} eventName
 * @param {*} handler
 */
function useListener( ref, eventName, handler ) {
	// eslint-disable-next-line consistent-return
	useEffect( () => {
		if ( ref.current ) {
			const element = ref.current;
			element.addEventListener( eventName, handler );
			return () => element.removeEventListener( eventName, handler );
		}
	}, [eventName, handler, ref] );
}

/**
 * The main component.
 *
 * @param {Object} props Props
 * @return {Object} JSX
 */
export const DatePicker = ( {
	onChange,
	onFocus,
	onBlur,
	onOpen,
	onClose,
	dateAdapter,
	localization,
	identifier,
	name,
	disabled,
	value,
	ariaDescribedby,
	min,
} ) => {
	const ref = useRef( null );

	/**
	 * Update input.
	 *
	 * @param {Node} input The input to use.
	 */
	const updateInput = ( input ) => {
		// Need this for addtional context for the user.
		input.setAttribute( 'aria-describedby', ariaDescribedby );

		// Morayify
		input.classList.add( 'form-control' );

		// Validation
		// Pattern source: https://www.html5pattern.com/Dates
		// Should validate YYYY-MM-DD
		input.setAttribute( 'pattern', '(?:19|20)[0-9]{2}-(?:(?:0[1-9]|1[0-2])-(?:0[1-9]|1[0-9]|2[0-9])|(?:(?!02)(?:0[1-9]|1[0-2])-(?:30))|(?:(?:0[13578]|1[02])-31))' );
	};

	/**
	 * Update button.
	 * @param {Node} button The button to use.
	 */
	const updateButton = ( button ) => {
		// Morayify
		button.classList.add( ...['btn', 'btn-sm', 'position-absolute', 'p-0', 'text-inverse'] );
	};

	useListener( ref, 'duetChange', onChange );
	useListener( ref, 'duetFocus', onFocus );
	useListener( ref, 'duetBlur', onBlur );
	useListener( ref, 'duetOpen', onOpen );
	useListener( ref, 'duetClose', onClose );

	useEffect( () => {
		const input = ref.current.querySelector( '.duet-date__input' );
		const button = ref.current.querySelector( '.duet-date__toggle ' );

		// Check if the input is already in the DOM.
		// If it is, we can just update it and not bother with the observer.
		if ( !input ) {
			// Observer
			const observer = new MutationObserver( () => {
				const input = ref.current.querySelector( '.duet-date__input' );

				// Check for each change.
				// Do a fresh query.
				if ( !input ) {
					return;
				}

				const button = ref.current.querySelector( '.duet-date__toggle ' );

				updateInput( input );
				updateButton( button );

				// We shouldn't need this again.
				observer.disconnect();
			} );

			// Start observing.
			observer.observe( ref.current, { subtree: true, childList: true } );
		} else {
			updateInput( input );
			updateButton( button );
		}
	} );

	return (
		<duet-date-picker
			ref={ref}
			localization={localization}
			dateAdapter={dateAdapter}
			identifier={identifier}
			name={name}
			disabled={disabled}
			value={value}
			min={min}
		/>
	);
};
